import {
  JobPicture,
  NewJobPicture,
  PictureType,
  ProjectOrJobPicture,
  ProjectPicture,
  RoutinePicture,
  RoutinePictureId,
  SpecialJobPicture,
  SpecialJobPictureId,
} from '~/common/entities/Pictures/PictureTypes';
import {
  JobPicturePutRequest,
  PicturePutRequest,
  PictureUploadRequest,
} from '~/common/entities/Pictures/PictureRequests';
import { useAuthenticationService } from './AuthenticationService';
import { ApiErrorCallback, ApiSuccessCallback } from './callback';
import { authenticatedApi } from '../utils/authenticatedApi';
import { useSpecialJobService } from './SpecialJobService';
import { useProjectsService } from './ProjectService';
import { useRoutineService } from './RoutineService';
import { createContainer } from 'unstated-next';
import { useState } from 'react';
import _ from 'lodash';

interface IdToPictureMap {
  [key: string]: ProjectOrJobPicture;
}

function isProjectPic(pic: ProjectOrJobPicture): pic is ProjectPicture {
  return _.has(pic, 'project');
}

function isJobPic(pic: ProjectOrJobPicture): pic is JobPicture {
  return _.has(pic, 'job');
}

function isRoutinePic(pic: ProjectOrJobPicture): pic is RoutinePicture {
  return _.has(pic, 'routine');
}

function isSpecialJob(pic: ProjectOrJobPicture): pic is SpecialJobPicture {
  return _.has(pic, 'special_job');
}

function isNewJob(pic: ProjectOrJobPicture): pic is NewJobPicture {
  return _.has(pic, 'new_job');
}

function usePicture() {
  const [picturesData, setPicturesData] = useState<IdToPictureMap>({});
  const { routines, setRoutines } = useRoutineService();
  const { specialJobs, setSpecialJobs } = useSpecialJobService();
  const projectService = useProjectsService();
  const { authenticationState } = useAuthenticationService();

  const setRotationOfProjectOrJobPicture = (
    currentPictureId: string,
    rotation: number,
  ) => {
    const pictureData = picturesData[currentPictureId];
    pictureData.rotation = rotation;
    setPicturesData((prev) => ({
      ...(_.set(prev, currentPictureId, pictureData) || {}),
    }));
  };

  const loadPicture = async (
    pictureId: string,
    pictureType: PictureType,
    onSuccess?: ApiSuccessCallback<ProjectOrJobPicture>,
    onError?: ApiErrorCallback,
  ) => {
    const [realId] = pictureId.split('-');
    if (_.has(picturesData, pictureId)) {
      return _.get(picturesData, pictureId);
    }
    let pictureUrl = '';
    if (pictureType === PictureType.PROJECT) pictureUrl = 'project/pic';
    else if (pictureType === PictureType.JOB) pictureUrl = 'job/pic';
    else if (pictureType === PictureType.ROUTINE) pictureUrl = 'routine/pic';
    else if (pictureType === PictureType.SPECIALJOB)
      pictureUrl = 'special_job/pic';
    else if (pictureType === PictureType.NEWJOB) pictureUrl = 'new_job/pic';
    const { data, error } = await authenticatedApi(
      authenticationState,
    ).get<ProjectOrJobPicture>(`/${pictureUrl}/${realId}/`);
    if (error) {
      if (onError) onError(error);
      return undefined;
    }
    if (onSuccess && data) onSuccess(data);

    if (data) {
      setPicturesData((state) => ({
        ..._.set(state || {}, pictureId, data),
      }));
      return data;
    }
    return undefined;
  };

  const uploadPictures = async (
    picturesData: string[],
    pictureType: PictureType,
    projectOrJobId: string,
    rotation?: number,
    onSuccess?: ApiSuccessCallback<ProjectOrJobPicture[]>,
    onError?: ApiErrorCallback,
  ) => {
    let pictureUrl = '';
    if (pictureType === PictureType.PROJECT) pictureUrl = 'project/add_pic';
    else if (pictureType === PictureType.JOB) pictureUrl = 'job/add_pic';
    else if (pictureType === PictureType.ROUTINE)
      pictureUrl = 'routine/add_pic';
    else if (pictureType === PictureType.SPECIALJOB)
      pictureUrl = 'special_job/add_pic';
    pictureUrl += `/${projectOrJobId}`;

    const responses = await Promise.all(
      picturesData.map((data) =>
        authenticatedApi(authenticationState).post<
          ProjectOrJobPicture,
          PictureUploadRequest
        >(`/${pictureUrl}/`, {
          picture: data,
          rotation,
        }),
      ),
    );

    const errors = [];

    responses.forEach((value) => {
      const { data, error } = value;
      if (error) {
        errors.push(error);
        return;
      }
      const { projects, updateProjectLocally } = projectService;

      if (isProjectPic(data)) {
        const project = projects.find((p) => p.id == data.project);
        if (project) {
          updateProjectLocally({
            ...project,
            project_pictures: [...(project.project_pictures || []), data],
          });
        }
      } else if (isJobPic(data)) {
        for (let i = 0; i < projects.length; i++) {
          const project = projects[i];
          const job = project.jobs?.find((job) => job.id == data.job);
          if (job) {
            updateProjectLocally({
              ...project,
              jobs: _.orderBy(
                [
                  ...project.jobs.filter((j) => j.id !== job.id),
                  {
                    ...job,
                    job_pictures: [...(job.job_pictures || []), data],
                  },
                ],
                [(job) => job.step_number],
              ),
            });
            break;
          }
        }
      } else if (isRoutinePic(data)) {
        const routinePictureId = data as unknown as RoutinePictureId;
        const routine = routines.find((r) => r.id == routinePictureId.routine);
        if (routine) {
          if (!routine.routine_pictures)
            routine.routine_pictures = [routinePictureId];
          else
            routine.routine_pictures = [
              ...(routine.routine_pictures || []),
              routinePictureId,
            ];
          setRoutines([
            ...routines.filter((r) => r.id != routine.id),
            { ...routine },
          ]);
        }
      } else if (isSpecialJob(data)) {
        const specialJobPictureId = data as unknown as SpecialJobPictureId;
        const specialJob = specialJobs.find(
          (sj) => sj.id == specialJobPictureId.special_job,
        );
        if (specialJob) {
          if (!specialJob.special_job_pictures)
            specialJob.special_job_pictures = [specialJobPictureId];
          else
            specialJob.special_job_pictures = [
              ...(specialJob.special_job_pictures || []),
              specialJobPictureId,
            ];

          setSpecialJobs([
            ...specialJobs.filter((sj) => sj.id != specialJob.id),
            specialJob,
          ]);
        }
      }
      setPicturesData((pics) =>
        _.set(pics || {}, `${data.id}-${pictureType}`, data),
      );
    });
    if (errors && onError) onError(errors as any);
    if (errors.length === 0 && responses && onSuccess) {
      onSuccess(responses.map((res) => res.data));
    }
  };

  const updatePicture = async (
    pictureId: string,
    pictureType: PictureType,
    rotation: number,
    onSuccess?: ApiSuccessCallback<any>,
    onError?: ApiErrorCallback,
  ) => {
    let pictureUrl = '';
    const [realId] = pictureId.split('-');
    if (pictureType === PictureType.PROJECT)
      pictureUrl = `/project/pic/${realId}/change/`;
    else if (pictureType === PictureType.JOB)
      pictureUrl = `/job/pic/${realId}/change/`;
    else if (pictureType === PictureType.ROUTINE)
      pictureUrl = `/routine/pic/${realId}/change/`;
    else if (pictureType === PictureType.SPECIALJOB)
      pictureUrl = `/special_job/pic/${realId}/change/`;
    else if (pictureType === PictureType.NEWJOB)
      pictureUrl = `/new_job/pic/${realId}/change/`;

    const values: PicturePutRequest = { rotation };
    const { data, error: ApiError } = await authenticatedApi(
      authenticationState,
    ).patch<any, PicturePutRequest>(pictureUrl, values);

    if (ApiError && onError) onError(ApiError);

    if (data && onSuccess) onSuccess(data);
  };

  const rotatePicture = (
    rotateToLeft: boolean,
    currentPictureId: string,
    pictureType: PictureType,
  ) => {
    const currentRotation = picturesData[currentPictureId].rotation;
    let newRotation;
    if (rotateToLeft) {
      newRotation = currentRotation - 90;
      if (newRotation < 0) newRotation = 270;
    } else {
      newRotation = currentRotation + 90;
      if (newRotation >= 360) newRotation = 0;
    }

    const rotatePictureLocallyOnSuccess = () => {
      setRotationOfProjectOrJobPicture(currentPictureId, newRotation);
    };

    updatePicture(
      currentPictureId,
      pictureType,
      newRotation,
      rotatePictureLocallyOnSuccess,
    );
  };

  const updateOrderNumber = async (
    pictureId: string,
    rotation: number,
    orderNumber: number,
    onSuccess?: ApiSuccessCallback<any>,
    onError?: ApiErrorCallback,
  ) => {
    const [realId] = pictureId.split('-');
    const pictureUrl = `/job/pic/${realId}/change/`;

    const values: JobPicturePutRequest = {
      rotation,
      //order_number begins on the server with 1
      order_number: orderNumber + 1,
    };
    const { data, error: ApiError } = await authenticatedApi(
      authenticationState,
    ).patch<any, JobPicturePutRequest>(pictureUrl, values);

    if (ApiError && onError) onError(ApiError);

    if (data && onSuccess) onSuccess(data);
  };

  const deletePicture = async (
    pictureId: string,
    pictureType: PictureType,
    projectOrJobId: string,
    onSuccess?: ApiSuccessCallback<boolean>,
    onError?: ApiErrorCallback,
  ) => {
    const [realId] = pictureId.split('-');
    let pictureUrl = '';
    if (pictureType === PictureType.PROJECT) pictureUrl = 'project/pic';
    else if (pictureType === PictureType.JOB) pictureUrl = 'job/pic';
    else if (pictureType === PictureType.ROUTINE) pictureUrl = 'routine/pic';
    else if (pictureType === PictureType.SPECIALJOB)
      pictureUrl = 'special_job/pic';
    else if (pictureType === PictureType.NEWJOB) pictureUrl = 'new_job/pic';
    const { error } = await authenticatedApi(authenticationState).delete<{}>(
      `/${pictureUrl}/${realId}/`,
    );
    if (error && onError) onError();
    if (!error) {
      setPicturesData((picturesData) => ({
        ...(_.omit(picturesData, pictureId) || {}),
      }));
    }
    const { projects, updateProjectLocally } = projectService;
    let isLast = false;
    if (pictureType === PictureType.PROJECT) {
      const project = projects.find((project) => project.id == projectOrJobId);
      if (project?.project_pictures?.length === 1) {
        isLast = true;
      }
      updateProjectLocally({
        ...project,
        project_pictures: [
          ...(project.project_pictures || []).filter(
            (pic) => pic.id == pictureId,
          ),
        ],
      });
    } else if (pictureType === PictureType.JOB) {
      for (let i = 0; i < projects.length; i++) {
        const project = projects[i];
        const job = project.jobs?.find((job) => job.id == projectOrJobId);
        if (job?.job_pictures?.length === 1) {
          isLast = true;
        }
        if (job) {
          updateProjectLocally({
            ...project,
            jobs: _.orderBy(
              [
                ...project.jobs.filter((j) => j.id !== job.id),
                {
                  ...job,
                  job_pictures: [
                    ...(job.job_pictures || []).filter(
                      (pic) => pic.id !== pictureId,
                    ),
                  ],
                },
              ],
              [(job) => job.step_number],
            ),
          });
          break;
        }
      }
    } else if (pictureType === PictureType.ROUTINE) {
      const routine = routines.find((routine) => routine.id == projectOrJobId);
      routine.routine_pictures = routine.routine_pictures.filter(
        (pictureId) => pictureId.id != realId,
      );
      setRoutines([...routines.filter((r) => r.id != routine.id), routine]);
    } else if (pictureType === PictureType.SPECIALJOB) {
      const specialJob = specialJobs.find(
        (routine) => routine.id == projectOrJobId,
      );
      specialJob.special_job_pictures = specialJob.special_job_pictures.filter(
        (pictureId) => pictureId.id != realId,
      );
      setSpecialJobs([
        ...specialJobs.filter((r) => r.id != specialJob.id),
        specialJob,
      ]);
    } else if (pictureType === PictureType.NEWJOB) {
      /*QUESTON: Kann man überhaupt Bilder von NewJob löschen?*/
    }

    if (!error && onSuccess) onSuccess(isLast);
  };

  return {
    picturesData,
    setPicturesData,
    setRotationOfProjectOrJobPicture,
    rotatePicture,
    loadPicture,
    uploadPictures,
    updatePicture,
    updateOrderNumber,
    deletePicture,
  };
}

export const { Provider: PictureProvider, useContainer: usePictureService } =
  createContainer(usePicture);
